import { useMemo, useState } from 'react'
import { generateApiRouletteSlots } from './scheduling'
import type { ApiRouletteSlice, ApiRouletteSlot } from './types'
import { ApiRouletteSectionHeading } from './ApiRouletteSectionHeading'
import styles from './ApiRoulette.module.css'

export function ApiRouletteScheduleEditor({
  slots,
  slices,
  timezone,
  disabled,
  onChange,
  onLog,
}: {
  slots: ApiRouletteSlot[]
  slices: ApiRouletteSlice[]
  timezone: string
  disabled: boolean
  onChange: (slots: ApiRouletteSlot[]) => void
  onLog: (id: string) => void
}) {
  const [batch, setBatch] = useState({
    from: '',
    until: '',
    start: '09:00',
    end: '18:00',
    count: 5,
    slice: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [dateFilter, setDateFilter] = useState('')
  const prizes = slices.filter((slice) => slice.kind === 'prize')
  const filtered = useMemo(
    () => slots.filter((slot) => !dateFilter || slot.date === dateFilter),
    [slots, dateFilter]
  )
  const pages = Math.max(1, Math.ceil(filtered.length / 20))
  const currentPage = Math.min(page, pages)
  const update = (id: string, patch: Partial<ApiRouletteSlot>) =>
    onChange(slots.map((slot) => (slot.id === id ? { ...slot, ...patch } : slot)))
  return (
    <section className={`${styles.section} ${styles.scheduleSection}`}>
      <ApiRouletteSectionHeading
        step="02"
        title="Horários vencedores"
        actions={
          <span className={styles.badge}>{slots.length} horários cadastrados</span>
        }
      >
        Escolha o dia, o horário e a fatia premiada de cada intervalo. Fuso da agenda:{' '}
        <strong>{timezone}</strong>.
      </ApiRouletteSectionHeading>
      <div className={styles.ruleNote}>
        <strong>Um prêmio por horário.</strong> O primeiro giro válido ganha; os seguintes
        ficam sem prêmio até o próximo horário. Cada intervalo termina no próximo horário
        do dia ou à meia-noite.
      </div>
      <details className={styles.generatorTool}>
        <summary>
          <span className={styles.toolIcon} aria-hidden="true">
            +
          </span>
          <span className={styles.toolDescription}>
            <strong>Gerar horários em vários dias</strong>
            <span>Ferramenta opcional para adicionar vários horários de uma só vez.</span>
          </span>
          <span className={styles.toolAction} aria-hidden="true">
            <span className={styles.toolClosed}>Abrir ferramenta</span>
            <span className={styles.toolOpen}>Recolher</span>
          </span>
          <span className={styles.toolChevron} aria-hidden="true" />
        </summary>
        <div className={styles.toolBody}>
          <fieldset disabled={disabled} className={styles.generator}>
            <legend className={styles.srOnly}>Parâmetros para gerar horários</legend>
            <div className={styles.grid}>
              <label>
                Primeiro dia
                <input
                  type="date"
                  value={batch.from}
                  onChange={(e) => setBatch({ ...batch, from: e.target.value })}
                />
              </label>
              <label>
                Último dia
                <input
                  type="date"
                  value={batch.until}
                  onChange={(e) => setBatch({ ...batch, until: e.target.value })}
                />
              </label>
              <label>
                Início do intervalo
                <input
                  type="time"
                  value={batch.start}
                  onChange={(e) => setBatch({ ...batch, start: e.target.value })}
                />
              </label>
              <label>
                Final do intervalo
                <input
                  type="time"
                  value={batch.end}
                  onChange={(e) => setBatch({ ...batch, end: e.target.value })}
                />
              </label>
              <label>
                Novos horários por dia
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={batch.count}
                  onChange={(e) => setBatch({ ...batch, count: Number(e.target.value) })}
                />
              </label>
              <label>
                Fatia vencedora
                <select
                  value={batch.slice}
                  onChange={(e) => setBatch({ ...batch, slice: e.target.value })}
                >
                  <option value="">Selecione</option>
                  {prizes.map((slice) => (
                    <option key={slice.id} value={slice.id}>
                      {slice.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className={styles.helperNote}>
              Horários aleatórios em minutos distintos, sem repetir os já cadastrados.
              Revise a agenda abaixo e salve a Roleta API para aplicar.
            </p>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => {
                try {
                  onChange(generateApiRouletteSlots(slots, batch))
                  setError(null)
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Revise os horários.')
                }
              }}
            >
              Gerar e adicionar horários
            </button>
          </fieldset>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </div>
      </details>
      <div className={styles.agendaHeading}>
        <h4>Agenda de premiação</h4>
        <p>Adicione um horário por vez ou utilize a ferramenta de geração acima.</p>
      </div>
      <div className={`${styles.toolbar} ${styles.agendaToolbar}`}>
        <label>
          Filtrar dia
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value)
              setPage(1)
            }}
          />
        </label>
        <button
          type="button"
          className={styles.secondaryButton}
          disabled={disabled || slots.length >= 5000 || !prizes.length}
          onClick={() => {
            setDateFilter('')
            setPage(Math.floor(slots.length / 20) + 1)
            onChange([
              ...slots,
              {
                id: crypto.randomUUID(),
                date: '',
                time: '',
                winning_slice_id: prizes[0].id,
                is_active: true,
              },
            ])
          }}
        >
          Adicionar horário manual
        </button>
      </div>
      <div className={styles.tableWrap}>
        <table>
          <caption className={styles.srOnly}>Horários vencedores cadastrados</caption>
          <thead>
            <tr>
              <th scope="col">Dia</th>
              <th scope="col">Horário</th>
              <th scope="col">Fatia vencedora</th>
              <th scope="col">Ativo</th>
              <th scope="col">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice((currentPage - 1) * 20, currentPage * 20).map((slot) => (
              <tr key={slot.id}>
                <td>
                  <input
                    aria-label="Dia do horário"
                    disabled={disabled}
                    type="date"
                    value={slot.date}
                    onChange={(e) => update(slot.id, { date: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label="Horário vencedor"
                    disabled={disabled}
                    type="time"
                    value={slot.time}
                    onChange={(e) => update(slot.id, { time: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    aria-label="Fatia vencedora do horário"
                    disabled={disabled}
                    value={slot.winning_slice_id}
                    onChange={(e) =>
                      update(slot.id, { winning_slice_id: e.target.value })
                    }
                  >
                    {prizes.map((slice) => (
                      <option key={slice.id} value={slice.id}>
                        {slice.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    aria-label="Horário ativo"
                    disabled={disabled}
                    type="checkbox"
                    checked={slot.is_active}
                    onChange={(e) => update(slot.id, { is_active: e.target.checked })}
                  />
                </td>
                <td className={styles.actions}>
                  <button type="button" onClick={() => onLog(slot.id)}>
                    Ver log
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(slots.filter((s) => s.id !== slot.id))}
                  >
                    Remover
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={5} className={styles.emptyState}>
                  <strong>
                    {slots.length
                      ? 'Nenhum horário neste dia.'
                      : 'Sua agenda ainda está vazia.'}
                  </strong>
                  <span>
                    {slots.length
                      ? 'Escolha outra data para consultar os horários.'
                      : 'Adicione um horário manual ou abra a ferramenta para gerar vários dias.'}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className={styles.toolbar}>
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => setPage(currentPage - 1)}
        >
          Anterior
        </button>
        <span>
          {currentPage} / {pages}
        </span>
        <button
          type="button"
          disabled={currentPage >= pages}
          onClick={() => setPage(currentPage + 1)}
        >
          Próxima
        </button>
      </div>
      <p className={styles.helperNote}>
        Horários iniciados preservam data, prêmio e histórico. Para interromper um
        intervalo, desative-o.
      </p>
    </section>
  )
}
